import { type FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibAttachIcon } from '@chewy/kib-icons-react'

import type { MessageAttachment as MessageAttachmentType } from '~/api/agentChat'

import styles from './MessageAttachment.module.scss'

interface MessageAttachmentProps {
  attachment: MessageAttachmentType
}

export const MessageAttachment: FC<MessageAttachmentProps> = ({
  attachment,
}) => {
  const { t } = useTranslation('Agent')
  const { data, mimeType } = attachment
  const [imageError, setImageError] = useState(false)
  const isImage = mimeType.startsWith('image/')

  const getFileLabel = (type: string): string => {
    if (type.includes('pdf')) {
      return t('Agent:FILE_TYPE_PDF')
    }
    if (type.includes('spreadsheet') || type.includes('excel')) {
      return t('Agent:FILE_TYPE_SPREADSHEET')
    }
    if (type.includes('word') || type.includes('document')) {
      return t('Agent:FILE_TYPE_DOCUMENT')
    }
    if (type.includes('text')) {
      return t('Agent:FILE_TYPE_TEXT')
    }
    return t('Agent:FILE_TYPE_GENERIC')
  }

  const imageData = useMemo(() => {
    if (!isImage || imageError) return null

    try {
      let cleanData = data
        .replace(/^data:[^;]+;base64,/, '')
        .replace(/\s/g, '')
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .replace(/[^A-Za-z0-9+/=]/g, '')

      // Return null for invalid data (too short)
      if (cleanData.length < 10) {
        return null
      }

      const padding = (4 - (cleanData.length % 4)) % 4
      cleanData += '='.repeat(padding)

      return `data:${mimeType};base64,${cleanData}`
    } catch {
      return null
    }
  }, [isImage, imageError, data, mimeType])

  if (imageData) {
    return (
      <div className={styles.imageAttachment}>
        <img
          alt={t('Agent:ATTACHMENT')}
          src={imageData}
          onError={() => setImageError(true)}
        />
      </div>
    )
  }

  return (
    <div className={styles.fileAttachment}>
      <KibAttachIcon height={24} width={24} />
      <div className={styles.fileLabel}>
        {imageError ? t('Agent:FILE_TYPE_IMAGE') : getFileLabel(mimeType)}
      </div>
    </div>
  )
}

export default MessageAttachment
